// Simple state management using localStorage
// Cart persists across browser refresh and app crashes

// ==================== Cart ====================
// Cart is stored per-property to prevent cross-property cart issues
const getCartKey = (propertyId) => `hm_cart_${propertyId || 'default'}`;

// Central property slug mappings and system reserved routes
export const PROPERTY_SLUGS = {
  "VaranasiHostel": "varanasi",
  "varanasi": "varanasi",
  "DarjeelingHostel": "darjeeling-hostel",
  "darjeeling-hostel": "darjeeling-hostel",
  "DarjeelingHome": "darjeeling-home",
  "darjeeling-home": "darjeeling-home"
};

const SYSTEM_ROUTES = new Set([
  "",
  "experiences",
  "food",
  "cart",
  "requests",
  "staff",
  "admin",
  "forgot-password",
]);

// Get current property for cart operations
const getCurrentPropertyForCart = () => {
  return getPropertyId();
};

export const getCart = (propertyId = null) => {
  try {
    const pid = propertyId || getCurrentPropertyForCart();
    const cart = localStorage.getItem(getCartKey(pid));
    const parsed = cart ? JSON.parse(cart) : [];
    // Validate cart structure
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(item => item && item.id && item.quantity > 0);
  } catch (error) {
    console.error("Error reading cart:", error);
    return [];
  }
};

const saveCart = (cart, propertyId = null) => {
  try {
    const pid = propertyId || getCurrentPropertyForCart();
    localStorage.setItem(getCartKey(pid), JSON.stringify(cart));
    // Also save timestamp for debugging
    localStorage.setItem(`${getCartKey(pid)}_updated`, new Date().toISOString());
  } catch (error) {
    console.error("Error saving cart:", error);
  }
};

export const addToCart = (item, propertyId = null) => {
  const cart = getCart(propertyId);
  const existingIndex = cart.findIndex((i) => i.id === item.id);
  
  if (existingIndex >= 0) {
    cart[existingIndex].quantity += 1;
  } else {
    cart.push({ 
      id: item.id,
      name: item.name,
      price: item.price,
      image_url: item.image_url,
      description: item.description,
      is_vegetarian: item.is_vegetarian,
      quantity: 1 
    });
  }
  
  saveCart(cart, propertyId);
  return cart;
};

export const updateCartQuantity = (itemId, quantity, propertyId = null) => {
  const cart = getCart(propertyId);
  const index = cart.findIndex((i) => i.id === itemId);
  
  if (index >= 0) {
    if (quantity <= 0) {
      cart.splice(index, 1);
    } else {
      cart[index].quantity = quantity;
    }
  }
  
  saveCart(cart, propertyId);
  return cart;
};

export const removeFromCart = (itemId, propertyId = null) => {
  const cart = getCart(propertyId).filter((i) => i.id !== itemId);
  saveCart(cart, propertyId);
  return cart;
};

export const clearCart = (propertyId = null) => {
  const pid = propertyId || getCurrentPropertyForCart();
  localStorage.removeItem(getCartKey(pid));
  return [];
};

export const getCartTotal = (propertyId = null) => {
  const cart = getCart(propertyId);
  return cart.reduce((total, item) => total + (item.price || 0) * (item.quantity || 0), 0);
};

export const getCartCount = (propertyId = null) => {
  const cart = getCart(propertyId);
  return cart.reduce((count, item) => count + (item.quantity || 0), 0);
};

// ==================== Property ====================
const PROPERTY_KEY = "hm_property";

export const getPropertyId = () => {
  // First check URL path for slug (e.g., /VaranasiHostel)
  const pathname = window.location.pathname;
  const slug = pathname.replace("/", "").split("?")[0].split("/")[0];
  if (PROPERTY_SLUGS[slug]) {
    const propertyId = PROPERTY_SLUGS[slug];
    try { localStorage.setItem(PROPERTY_KEY, propertyId); } catch(e) {}
    return propertyId;
  }
  if (slug && !SYSTEM_ROUTES.has(slug.toLowerCase())) {
    return slug;
  }
  
  // Check URL params
  const urlParams = new URLSearchParams(window.location.search);
  const propertyFromUrl = urlParams.get("property");
  
  if (propertyFromUrl) {
    try { localStorage.setItem(PROPERTY_KEY, propertyFromUrl); } catch(e) {}
    return propertyFromUrl;
  }
  
  // Fall back to localStorage
  try { return localStorage.getItem(PROPERTY_KEY) || ""; } catch(e) { return ""; }
};

export const setPropertyId = (propertyId) => {
  try { localStorage.setItem(PROPERTY_KEY, propertyId); } catch(e) {}
};

export const getPropertySlug = (propertyId = null) => {
  const pid = propertyId || getPropertyId();
  if (!pid) return "";
  // Reverse lookup
  for (const [slug, id] of Object.entries(PROPERTY_SLUGS)) {
    if (id === pid) return slug;
  }
  return pid || "";
};

// ==================== User & Auth Helpers ====================
const SESSION_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours strict session expiry
const SESSION_TIMESTAMP_KEY = "hm_session_login_time";

export const recordSessionLogin = () => {
  try {
    localStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString());
  } catch(e) {}
};

export const isSessionExpired = () => {
  try {
    const time = localStorage.getItem(SESSION_TIMESTAMP_KEY);
    if (!time) return false;
    return Date.now() - parseInt(time, 10) > SESSION_EXPIRY_MS;
  } catch {
    return false;
  }
};

export const getCurrentUser = () => {
  try {
    if (isSessionExpired()) {
      logoutAdmin();
      logoutStaff();
      return null;
    }
    const raw = localStorage.getItem("hm_user");
    if (raw) return JSON.parse(raw);
    if (localStorage.getItem(ADMIN_AUTH_KEY) === "true") {
      return { id: "admin", username: "Tech", role: "admin" };
    }
    return null;
  } catch {
    return null;
  }
};

// ==================== Admin Auth ====================
const ADMIN_AUTH_KEY = "hm_admin_auth";

export const isAdminAuthenticated = () => {
  try {
    if (isSessionExpired()) {
      logoutAdmin();
      logoutStaff();
      return false;
    }
    const user = getCurrentUser();
    if (user) {
      if (user.role === "admin" || user.role === "global") return true;
      if (
        user.can_manage_settings ||
        user.can_edit_menu ||
        user.can_manage_experiences ||
        user.can_manage_users ||
        user.can_view_reports ||
        user.can_view_analytics ||
        user.can_manage_discounts ||
        user.can_manage_qrcodes ||
        user.can_view_logs ||
        user.can_edit_messages
      ) {
        return true;
      }
      return false;
    }
    return localStorage.getItem(ADMIN_AUTH_KEY) === "true";
  } catch(e) {
    return false;
  }
};

export const setAdminAuthenticated = (value) => {
  if (value) {
    try { 
      localStorage.setItem(ADMIN_AUTH_KEY, "true"); 
      recordSessionLogin();
    } catch(e) {}
  } else {
    try { localStorage.removeItem(ADMIN_AUTH_KEY); } catch(e) {}
  }
};

export const logoutAdmin = () => {
  try {
    localStorage.removeItem(ADMIN_AUTH_KEY);
    localStorage.removeItem("hm_user");
    localStorage.removeItem("hm_token");
    localStorage.removeItem("hm_admin_token");
    localStorage.removeItem("hm_auth_token");
    localStorage.removeItem(SESSION_TIMESTAMP_KEY);
  } catch(e) {}
};

// ==================== Staff Auth ====================
const STAFF_AUTH_KEY = "hm_staff_auth";

export const getStaffAuth = () => {
  try {
    if (isSessionExpired()) {
      logoutAdmin();
      logoutStaff();
      return null;
    }
    const auth = localStorage.getItem(STAFF_AUTH_KEY);
    return auth ? JSON.parse(auth) : null;
  } catch {
    return null;
  }
};

export const setStaffAuth = (user) => {
  if (user) {
    try { 
      localStorage.setItem(STAFF_AUTH_KEY, JSON.stringify(user)); 
      recordSessionLogin();
    } catch(e) {}
  } else {
    try { localStorage.removeItem(STAFF_AUTH_KEY); } catch(e) {}
  }
};

export const logoutStaff = () => {
  try { 
    localStorage.removeItem(STAFF_AUTH_KEY); 
    localStorage.removeItem("hm_user");
    localStorage.removeItem("hm_auth_token");
    localStorage.removeItem(SESSION_TIMESTAMP_KEY);
  } catch(e) {}
};

// ==================== Guest Info (persistent storage) ====================
const GUEST_INFO_KEY = "hm_guest_info";

export const getGuestInfo = () => {
  try {
    const info = localStorage.getItem(GUEST_INFO_KEY);
    return info ? JSON.parse(info) : { name: "", room: "", whatsapp: "" };
  } catch {
    return { name: "", room: "", whatsapp: "" };
  }
};

export const setGuestInfo = (info) => {
  try {
    localStorage.setItem(GUEST_INFO_KEY, JSON.stringify(info));
  } catch (error) {
    console.error("Error saving guest info:", error);
  }
};

// ==================== Pending Order (for crash recovery) ====================
const PENDING_ORDER_KEY = "hm_pending_order";

export const savePendingOrder = (orderData) => {
  try {
    localStorage.setItem(PENDING_ORDER_KEY, JSON.stringify({
      ...orderData,
      savedAt: new Date().toISOString()
    }));
  } catch (error) {
    console.error("Error saving pending order:", error);
  }
};

export const getPendingOrder = () => {
  try {
    const order = localStorage.getItem(PENDING_ORDER_KEY);
    if (!order) return null;
    
    const parsed = JSON.parse(order);
    // Expire after 24 hours
    const savedAt = new Date(parsed.savedAt);
    const now = new Date();
    if (now - savedAt > 24 * 60 * 60 * 1000) {
      clearPendingOrder();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const clearPendingOrder = () => {
  localStorage.removeItem(PENDING_ORDER_KEY);
};
